import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from './Icon'
import HTMLReactParser from 'html-react-parser'
import { DashboardAggregate } from '@ivy-monorepo/shared'

interface AggregateBoxProps {
  title: string
  data: DashboardAggregate | undefined
  to?: string
}

const numberTriplets = /\B(?=(\d{3})+(?!\d))/g

const AggregateBox = ({ title, data, to }: AggregateBoxProps) => {
  const [percentChange, setPercentChange] = useState<number>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!data) {
      return
    }

    const currentValue = Number(data.currentValue)
    const previousValue = Number(data.previousValue)

    if (currentValue === 0 && previousValue === 0) {
      setPercentChange(0)
    } else if (previousValue === 0) {
      setPercentChange(Infinity)
    } else {
      const change = ((currentValue - previousValue) / previousValue) * 100
      setPercentChange(change)
    }
  }, [data])

  const comparisons = new Map<string, string>([
    ['day', `over previous day`],
    ['week', `over previous week`],
    ['month', `over previous month`],
    ['quarter', `over previous quarter`],
    ['year', `over previous year`],
  ])

  const formatNumber = (value: number, type: string) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0'
    }

    if (type === 'Money') {
      return `$${value.toFixed(2).replace(numberTriplets, ',')}`
    } else if (type === 'Integer') {
      return `${value.toFixed(0).replace(numberTriplets, ',')}`
    } else if (type === 'Percentage') {
      return `${value.toFixed(1).replace(numberTriplets, ',')}%`
    } else {
      return value.toString()
    }
  }

  return data === undefined ? null : (
    <div className="aggregate col-lg-3 col-md-6">
      <div
        className={to ? 'clickable' : undefined}
        onClick={() => {
          if (to) navigate(to)
        }}
        role={to ? 'link' : undefined}
        tabIndex={to ? 0 : undefined}
      >
        <span className="header">{title}</span>
        <p>{formatNumber(Number(data.currentValue), data.valueType)}</p>
        {data.comparisonPeriod.toLowerCase() !== 'none' && (
          <p>
            {(percentChange ?? 0) >= 0 ? (
              <Icon name="ArrowTrendUp" />
            ) : (
              <Icon name="ArrowTrendDown" />
            )}
            <>
              <span className={(percentChange ?? 0) >= 0 ? 'trendUp' : 'trendDown'}>
                {HTMLReactParser(
                  percentChange !== Infinity && percentChange !== undefined
                    ? percentChange.toFixed(1).toString()
                    : '<span className="infinity">&infin;</span>'
                )}
                %
              </span>
              <span> {comparisons.get(data.comparisonPeriod.toLowerCase() ?? 'day')}</span>
            </>
          </p>
        )}
      </div>
    </div>
  )
}

export default AggregateBox
